import { UMBRALES_IVT } from './umbralesVITA';
export function buildTerritorialState(scoring = {}, actor = {}) {
  const {
    ICT = 0,
    IVT = 0
  } = scoring;

  // ─────────────────────────────────────────────
  // PRIORIDAD 1: Estado declarado manualmente
  // Si el analista lo seteó en el formulario, respetarlo
  // ─────────────────────────────────────────────
  if (actor.estadoTerritorial) {
    return buildFromDeclared(actor.estadoTerritorial);
  }

  // ─────────────────────────────────────────────
  // PRIORIDAD 2: Alerta crítica detectada por engine
  // Sube automáticamente a Activación como mínimo.
  // Esto SÍ puede empujar el estado por encima del IVT,
  // porque una alerta crítica es un hecho declarado por
  // el analista, no un sub-índice inferido.
  // ─────────────────────────────────────────────
  if (actor._tieneAlertaCritica) {
    if (IVT >= 65 || ICT >= 55) {
      return {
        state: 'Convergencia Narrativa',
        color: '#f97316',
        severity: 'Alta',
        description: 'Alerta crítica activa con convergencia narrativa detectada.'
      };
    }
    return {
      state: 'Activación Territorial',
      color: '#eab308',
      severity: 'Media-Alta',
      description: 'Alerta crítica activa — señales de presión territorial creciente.'
    };
  }

  // ─────────────────────────────────────────────
  // PRIORIDAD 3: Scoring cognitivo calculado
  // El ESTADO se rige por el IVT (índice central) usando la
  // tabla única de umbralesVITA.js. Los sub-índices (IAT, ICT,
  // IST, IPN) NO pueden saltarse el IVT y empujar el estado por
  // su cuenta — eso producía contradicciones visibles (ej.
  // estado "Activación" con IVT en zona "Observación").
  //
  // Si un actor con un sub-índice alto debe pesar más, ese peso
  // se sube en el IVT (buildTerritorialScoring), no aquí. Así el
  // estado y el número central siempre cuentan la misma historia.
  // ─────────────────────────────────────────────

  // Escalamiento: IVT en zona crítica.
  if (IVT >= UMBRALES_IVT.ESCALAMIENTO) {
    return {
      state: 'Escalamiento Territorial',
      color: '#ef4444',
      severity: 'Crítica',
      description: 'Convergencia territorial activa con potencial de escalamiento.'
    };
  }

  // Convergencia: IVT en zona de convergencia.
  if (IVT >= UMBRALES_IVT.CONVERGENCIA) {
    return {
      state: 'Convergencia Narrativa',
      color: '#f97316',
      severity: 'Alta',
      description: 'Alineación narrativa detectada entre actores territoriales relevantes.'
    };
  }

  // Activación: IVT en zona de activación.
  if (IVT >= UMBRALES_IVT.ACTIVACION) {
    return {
      state: 'Activación Territorial',
      color: '#eab308',
      severity: 'Media-Alta',
      description: 'Señales crecientes de movilización y presión territorial.'
    };
  }

  // Sensibilidad: IVT en zona sensible.
  if (IVT >= UMBRALES_IVT.SENSIBILIDAD) {
    return {
      state: 'Sensibilidad Creciente',
      color: '#22c55e',
      severity: 'Media',
      description: 'El territorio presenta sensibilidad creciente frente al proyecto.'
    };
  }

  // Observación: primeras señales.
  if (IVT >= UMBRALES_IVT.OBSERVACION) {
    return {
      state: 'Observación',
      color: '#06b6d4',
      severity: 'Baja',
      description: 'Señales territoriales iniciales que requieren seguimiento.'
    };
  }

  return {
    state: 'Estable',
    color: '#64748b',
    severity: 'Baja',
    description: 'No se observan señales territoriales relevantes actualmente.'
  };
}

// ─────────────────────────────────────────────
// Mapea estado declarado por analista
// a estructura estándar con color y severidad
// ─────────────────────────────────────────────
function buildFromDeclared(estadoDeclarado) {
  const map = {
    'Escalamiento Territorial': {
      color: '#ef4444',
      severity: 'Crítica',
      description: 'Estado declarado por analista: escalamiento territorial activo.'
    },
    'Convergencia Narrativa': {
      color: '#f97316',
      severity: 'Alta',
      description: 'Estado declarado por analista: convergencia narrativa detectada.'
    },
    'Activación Territorial': {
      color: '#eab308',
      severity: 'Media-Alta',
      description: 'Estado declarado por analista: activación territorial en curso.'
    },
    'Sensibilidad Creciente': {
      color: '#22c55e',
      severity: 'Media',
      description: 'Estado declarado por analista: sensibilidad territorial creciente.'
    },
    'Observación': {
      color: '#06b6d4',
      severity: 'Baja',
      description: 'Estado declarado por analista: en observación territorial.'
    },
    'Contención Territorial': {
      color: '#8b5cf6',
      severity: 'Media',
      description: 'Estado declarado por analista: contención territorial activa.'
    },
    'Estabilización': {
      color: '#06b6d4',
      severity: 'Baja',
      description: 'Estado declarado por analista: territorio en proceso de estabilización.'
    },
    'Estable': {
      color: '#64748b',
      severity: 'Baja',
      description: 'Estado declarado por analista: territorio estable.'
    }
  };

  const found = map[estadoDeclarado];

  if (found) {
    return { state: estadoDeclarado, ...found };
  }

  return {
    state: estadoDeclarado,
    color: '#64748b',
    severity: 'Baja',
    description: `Estado territorial: ${estadoDeclarado}.`
  };
}